import { Component, OnInit, signal, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PlaygroundTestFormComponent } from '../playground-test-form/playground-test-form.component';
import { PlaygroundService } from '../../../../services/playground.service';
import { TaxonomyApiService } from '../../../../services/api-services/taxonomy-api.service';
import { NotificationService } from '../../../../services/notification.service';
import { ConfirmationOverlayService } from '../../../../services/confirmation-overlay.service';
import { TaxonomyStackLoaderComponent } from '../../../../components/taxonomy-stack-loader/taxonomy-stack-loader.component';
import { TestCardLoaderComponent } from '../../../../components/test-card-loader/test-card-loader.component';
import { UtcDateTimePipe } from "../../../../pipes/utc-date-time.pipe";

interface TestCard {
  test_id: string;
  test_label: string;
  test_status: 'processed' | 'in_progress' | 'queued';
  test_timestamp: string;
}

interface Taxonomy {
  taxonomy_id: string;
  taxonomy_name: string;
  test: TestCard[];
}

@Component({
  selector: 'app-playground-slider',
  standalone: true,
  imports: [CommonModule, PlaygroundTestFormComponent, TaxonomyStackLoaderComponent, TestCardLoaderComponent, UtcDateTimePipe],
  templateUrl: './playground-slider.component.html',
  styleUrl: './playground-slider.component.scss'
})
export class PlaygroundSliderComponent implements OnInit {
  @Output() taxonomySelected = new EventEmitter<string | null>();
  @Output() testSelected = new EventEmitter<{ taxonomyId: string; testId: string } | null>();
  @Output() testCreated = new EventEmitter<{ taxonomyId: string; testId: string }>();

  taxonomies = signal<Taxonomy[]>([]);
  expandedTaxonomies = signal<Set<string>>(new Set());
  openTestMenuId = signal<string | null>(null);
  showTestForm = signal<boolean>(false);
  isLoading = signal<boolean>(true);
  isLoadingTests = signal<Set<string>>(new Set());
  selectedTest = signal<{ taxonomyId: string; testId: string } | null>(null);

  constructor(
    private playgroundService: PlaygroundService,
    private taxonomyApiService: TaxonomyApiService,
    private notificationService: NotificationService,
    private confirmationOverlayService: ConfirmationOverlayService
  ) {}

  ngOnInit() {
    this.loadTaxonomies();
  }

  // Load taxonomies from API
  loadTaxonomies() {
    this.isLoading.set(true);
    this.taxonomyApiService.getTaxonomyV2({}).subscribe({
      next: (response: any) => {
        if (response?.data) {
          // Transform API response to match our interface
          const transformedTaxonomies = response.data.map((taxonomy: any) => ({
            taxonomy_id: taxonomy.taxonomy_id,
            taxonomy_name: taxonomy.taxonomy_name,
            test: [] // Initialize with empty tests array
          }));
          this.taxonomies.set(transformedTaxonomies);
        }
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Failed to load taxonomies:', error);
        this.notificationService.error('Error', 'Failed to load taxonomies');
        this.isLoading.set(false);
      }
    });
  }

  // Load test status for a specific taxonomy
  loadTestStatus(taxonomyId: string) {
    // Set loading state for this taxonomy's tests
    const currentLoadingTests = this.isLoadingTests();
    currentLoadingTests.add(taxonomyId);
    this.isLoadingTests.set(currentLoadingTests);

    // Clear previous test data for this taxonomy to show only loader
    const currentTaxonomies = this.taxonomies();
    const updatedTaxonomies = currentTaxonomies.map(taxonomy => {
      if (taxonomy.taxonomy_id === taxonomyId) {
        return {
          ...taxonomy,
          test: [] // Clear previous test data
        };
      }
      return taxonomy;
    });
    this.taxonomies.set(updatedTaxonomies);

    // Clear selected test if it was from this taxonomy
    const currentSelectedTest = this.selectedTest();
    if (currentSelectedTest && currentSelectedTest.taxonomyId === taxonomyId) {
      this.selectedTest.set(null);
      this.testSelected.emit(null);
    }

    this.playgroundService.getTestStatus(taxonomyId).subscribe({
      next: (response: any) => {
        if (response?.data?.tests) {
          // Map the actual API response fields to our test card structure
          const mappedTests = response.data.tests.map((test: any) => ({
            test_id: test.BatchId,
            test_label: test.Label,
            test_status: test.Status,
            test_timestamp: test.ProcessedTimestamp || test.UpdatedAt // Use ProcessedTimestamp, fallback to UpdatedAt
          }));

          // Update the taxonomy with mapped test data
          const currentTaxonomies = this.taxonomies();
          const updatedTaxonomies = currentTaxonomies.map(taxonomy => {
            if (taxonomy.taxonomy_id === taxonomyId) {
              return {
                ...taxonomy,
                test: mappedTests
              };
            }
            return taxonomy;
          });
          this.taxonomies.set(updatedTaxonomies);
        }

        // Remove loading state for this taxonomy's tests
        const updatedLoadingTests = this.isLoadingTests();
        updatedLoadingTests.delete(taxonomyId);
        this.isLoadingTests.set(updatedLoadingTests);
      },
      error: (error) => {
        console.error('Failed to load test status:', error);
        this.notificationService.error('Error', 'Failed to load test status');

        // Remove loading state for this taxonomy's tests on error
        const updatedLoadingTests = this.isLoadingTests();
        updatedLoadingTests.delete(taxonomyId);
        this.isLoadingTests.set(updatedLoadingTests);
      }
    });
  }

  onCreateTestClick() {
    this.taxonomyApiService.canCreateTest().subscribe({
      next: (response: any) => {
        if (response?.success && response?.data?.enable_create_test) {
          this.showTestForm.set(true);
        } else {
          this.confirmationOverlayService.showConfirmation(
            'Test Creation Limit',
            'A maximum of 5 tests are allowed. Please wait until one completes before starting another.',
            false,
            true
          ).then(() => {
            // User clicked OK, do nothing
          });
        }
      },
      error: (error: any) => {
        console.error('Failed to check test creation limit:', error);
        this.notificationService.error('Error', 'Failed to check test creation limit');
      }
    });
  }

  onTestFormClosed() {
    this.showTestForm.set(false);
  }

  // Handle when a new test is created
  onTestCreated(testInfo: { taxonomyId: string; testId: string }) {
    // Automatically expand the taxonomy if it's not already expanded
    const currentExpandedTaxonomies = this.expandedTaxonomies();
    if (!currentExpandedTaxonomies.has(testInfo.taxonomyId)) {
      const updatedExpandedTaxonomies = new Set(currentExpandedTaxonomies);
      updatedExpandedTaxonomies.add(testInfo.taxonomyId);
      this.expandedTaxonomies.set(updatedExpandedTaxonomies);

      // Emit the selected taxonomy ID
      this.taxonomySelected.emit(testInfo.taxonomyId);
    }

    // Automatically refresh the test status for this taxonomy
    this.loadTestStatus(testInfo.taxonomyId);

    // Emit the test created event to parent component
    this.testCreated.emit(testInfo);

    // Close the test form
    this.showTestForm.set(false);
  }

  toggleTaxonomy(taxonomyId: string) {
    const currentExpandedTaxonomies = this.expandedTaxonomies();
    const updatedExpandedTaxonomies = new Set(currentExpandedTaxonomies);

    if (updatedExpandedTaxonomies.has(taxonomyId)) {
      // Collapse taxonomy
      updatedExpandedTaxonomies.delete(taxonomyId);
    } else {
      // Expand taxonomy and load test status
      updatedExpandedTaxonomies.add(taxonomyId);
      this.loadTestStatus(taxonomyId);
    }

    this.expandedTaxonomies.set(updatedExpandedTaxonomies);

    // Emit the selected taxonomy ID or null if deselected
    if (updatedExpandedTaxonomies.has(taxonomyId)) {
      this.taxonomySelected.emit(taxonomyId);
    } else {
      this.taxonomySelected.emit(null);
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'processed':
        return 'Processed';
      case 'in_progress':
        return 'In Progress';
      case 'queued':
        return 'Test Queued';
      default:
        return '';
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'processed':
        return '#8595A6';
      case 'in_progress':
        return '#FF6B35';
      case 'queued':
        return '#8595A6';
      default:
        return '#8595A6';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'processed':
        return 'assets/icons/playground/test_processed.svg';
      case 'in_progress':
        return 'assets/icons/playground/test_in_progress.svg';
      case 'queued':
        return 'assets/icons/playground/test_in_queued.svg';
      default:
        return 'assets/icons/playground/test_processed.svg';
    }
  }

  toggleTestMenu(testId: string, event: Event) {
    event.stopPropagation();
    const currentOpenMenu = this.openTestMenuId();

    if (currentOpenMenu === testId) {
      this.openTestMenuId.set(null);
    } else {
      this.openTestMenuId.set(testId);
    }
  }

  rerunTest(testId: string) {
    // Find the taxonomy for this test to show loading state
    const currentTaxonomies = this.taxonomies();
    const currentTaxonomy = currentTaxonomies.find(t => t.test.some(test => test.test_id === testId));

    if (currentTaxonomy) {
      // Set loading state for this taxonomy's tests
      const currentLoadingTests = this.isLoadingTests();
      currentLoadingTests.add(currentTaxonomy.taxonomy_id);
      this.isLoadingTests.set(currentLoadingTests);

      // Clear previous test data for this taxonomy to show only loader
      const updatedTaxonomies = currentTaxonomies.map(taxonomy => {
        if (taxonomy.taxonomy_id === currentTaxonomy.taxonomy_id) {
          return {
            ...taxonomy,
            test: [] // Clear previous test data
          };
        }
        return taxonomy;
      });
      this.taxonomies.set(updatedTaxonomies);

      // Clear selected test if it was from this taxonomy
      const currentSelectedTest = this.selectedTest();
      if (currentSelectedTest && currentSelectedTest.taxonomyId === currentTaxonomy.taxonomy_id) {
        this.selectedTest.set(null);
        this.testSelected.emit(null);
      }
    }

    // Call API to rerun test
    this.taxonomyApiService.rerunTest({ batch_id: testId }).subscribe({
      next: (response: any) => {
        if (response?.success) {
          this.notificationService.success('Success', 'Test rerun initiated successfully');
          // Refresh the test status for the current taxonomy
          if (currentTaxonomy) {
            this.loadTestStatus(currentTaxonomy.taxonomy_id);
          }
        } else {
          this.notificationService.error('Error', 'Failed to rerun test');
          // Remove loading state on error
          if (currentTaxonomy) {
            const updatedLoadingTests = this.isLoadingTests();
            updatedLoadingTests.delete(currentTaxonomy.taxonomy_id);
            this.isLoadingTests.set(updatedLoadingTests);
          }
        }
      },
      error: (error) => {
        console.error('Failed to rerun test:', error);
        this.notificationService.error('Error', 'Failed to rerun test');
        // Remove loading state on error
        if (currentTaxonomy) {
          const updatedLoadingTests = this.isLoadingTests();
          updatedLoadingTests.delete(currentTaxonomy.taxonomy_id);
          this.isLoadingTests.set(updatedLoadingTests);
        }
      }
    });
    this.openTestMenuId.set(null);
  }

  deleteTest(testId: string) {
    // Find the taxonomy for this test to show loading state
    const currentTaxonomies = this.taxonomies();
    const currentTaxonomy = currentTaxonomies.find(t => t.test.some(test => test.test_id === testId));

    // Show confirmation popup before deleting
    this.confirmationOverlayService.showConfirmation(
      'Delete Test',
      'Are you sure you want to delete this test?',
      false,
      false
    ).then((result) => {
      if (result === true) {
        // User confirmed deletion, show loading state
        if (currentTaxonomy) {
          const currentLoadingTests = this.isLoadingTests();
          currentLoadingTests.add(currentTaxonomy.taxonomy_id);
          this.isLoadingTests.set(currentLoadingTests);

          // Clear previous test data for this taxonomy to show only loader
          const updatedTaxonomies = currentTaxonomies.map(taxonomy => {
            if (taxonomy.taxonomy_id === currentTaxonomy.taxonomy_id) {
              return {
                ...taxonomy,
                test: [] // Clear previous test data
              };
            }
            return taxonomy;
          });
          this.taxonomies.set(updatedTaxonomies);

          // Clear selected test if it was from this taxonomy
          const currentSelectedTest = this.selectedTest();
          if (currentSelectedTest && currentSelectedTest.taxonomyId === currentTaxonomy.taxonomy_id) {
            this.selectedTest.set(null);
            this.testSelected.emit(null);
          }
        }

        // Proceed with API call
        this.taxonomyApiService.deleteTest({ batch_id: testId }).subscribe({
          next: (response: any) => {
            if (response?.success) {
              this.notificationService.success('Success', 'Test deleted successfully');
              // Refresh the test status for the current taxonomy
              if (currentTaxonomy) {
                this.loadTestStatus(currentTaxonomy.taxonomy_id);
              }
            } else {
              this.notificationService.error('Error', 'Failed to delete test');
              // Remove loading state on error
              if (currentTaxonomy) {
                const updatedLoadingTests = this.isLoadingTests();
                updatedLoadingTests.delete(currentTaxonomy.taxonomy_id);
                this.isLoadingTests.set(updatedLoadingTests);
              }
            }
          },
          error: (error: any) => {
            console.error('Failed to delete test:', error);
            this.notificationService.error('Error', 'Failed to delete test');
            // Remove loading state on error
            if (currentTaxonomy) {
              const updatedLoadingTests = this.isLoadingTests();
              updatedLoadingTests.delete(currentTaxonomy.taxonomy_id);
              this.isLoadingTests.set(updatedLoadingTests);
            }
          }
        });
      }
    });
    this.openTestMenuId.set(null);
  }

  closeTestMenu() {
    this.openTestMenuId.set(null);
  }

  isTaxonomyExpanded(taxonomyId: string): boolean {
    return this.expandedTaxonomies().has(taxonomyId);
  }

  onTestCardClick(taxonomyId: string, testId: string) {
    this.selectedTest.set({ taxonomyId, testId });
    this.testSelected.emit({ taxonomyId, testId });
  }

  onNoTestsClick(taxonomyId: string) {
    this.selectedTest.set({ taxonomyId, testId: 'no-tests' });
    this.testSelected.emit({ taxonomyId, testId: 'no-tests' });
  }
}
